import { Autocomplete } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import {
  type MessagePathNode,
  type TopicNode,
  pathParser as parser,
  type PathMatch,
  pathMatcher as matcher,
} from "@/shared/domain/topics";

import styles from "./PathSelector.module.css";
import { PathSelectorInput } from "./PathSelectorInput";

export interface PathSelectorProps {
  className?: classNames.Argument;
  initialInputValue?: string;
  onChange: (value: MessagePathNode) => void;

  /** Called when the user clears the current selection. */
  onClear?: () => void;

  options: TopicNode[];
  optionFilter?: (messagePath: MessagePathNode) => boolean;

  /**
   * Optional comparator used to sort path match suggestions.
   * Applied as a secondary sort that preserves the default ordering
   * (complete matches first, then incomplete matches).
   */
  optionSort?: (a: MessagePathNode, b: MessagePathNode) => number;
}

export const PathSelector = React.forwardRef(function PathSelector(
  {
    className,
    initialInputValue,
    onChange,
    onClear,
    options,
    optionFilter,
    optionSort,
  }: PathSelectorProps,
  ref: React.ForwardedRef<HTMLElement>,
) {
  const [inputValue, setInputValue] = React.useState(initialInputValue ?? "");
  const [selection, setSelection] = React.useState<PathMatch | null>(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const parsedInput = React.useMemo(
    () => parser.parse(inputValue),
    [inputValue],
  );
  const matches = React.useMemo<PathMatch[]>(() => {
    const results = matcher.findMatches(parsedInput, options, optionFilter);
    if (optionSort) {
      // Apply as secondary sort, preserving the existing complete-first ordering
      results.sort((a, b) => {
        if (a.isComplete !== b.isComplete) {
          return a.isComplete ? -1 : 1;
        }
        return optionSort(a.match, b.match);
      });
    }
    return results;
  }, [parsedInput, options, optionFilter, optionSort]);

  // Expect 0 or 1 complete matches.
  const firstCompleteMatch = React.useMemo(
    () => matches.find((match) => match.isComplete),
    [matches],
  );

  const onClose = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const onInput = React.useCallback(
    (_: React.SyntheticEvent, value: string) => {
      setInputValue(value);
      // If the input is cleared (e.g., by clicking the X button), call onClear
      if (value === "" && inputValue !== "") {
        onClear?.();
      }
    },
    [inputValue, onClear],
  );

  const onOpen = React.useCallback(() => setIsOpen(true), []);

  const onSelect = React.useCallback(
    (_event: React.SyntheticEvent, value: string | PathMatch | null) => {
      if (value === null) {
        setSelection(null);
        return;
      }
      if (typeof value !== "string") {
        setSelection(value);
        if (value.match !== undefined) {
          onChange(value.match);
        }
      }
    },
    [onChange],
  );

  return (
    <Autocomplete
      autoHighlight
      className={classNames(className)}
      filterOptions={(x) => x} // https://v5.mui.com/material-ui/react-autocomplete/#search-as-you-type
      freeSolo
      getOptionLabel={(option: string | PathMatch) => {
        if (typeof option === "string") {
          return option;
        }

        return option.parts.map((part) => part.substring).join("");
      }}
      inputValue={inputValue}
      isOptionEqualToValue={(option: PathMatch, value: PathMatch) => {
        return option.match.equals(value.match);
      }}
      onChange={onSelect}
      onClose={onClose}
      onInputChange={onInput}
      onOpen={onOpen}
      openOnFocus
      options={matches}
      ref={ref}
      renderInput={(params) => (
        <PathSelectorInput
          hasExactMatch={firstCompleteMatch !== undefined}
          inputValue={inputValue}
          inputParams={params}
          isValid={matches.length > 0}
          label={matches.length > 0 ? "Path" : "No matching data found"}
          selectorIsOpen={isOpen}
        />
      )}
      renderOption={(props, option) => {
        return (
          <li
            key={option.match.id}
            {...props}
            className={classNames(props.className)}
          >
            <div className={styles.optionLine}>
              {option.parts.slice(0, -1).map((part, idx) => (
                <span
                  key={`${idx}#${part.substring}`}
                  className={classNames(styles.part, {
                    [styles.highlight]: part.isMatch,
                  })}
                >
                  {part.substring}
                </span>
              ))}
              <span
                className={classNames(styles.lastPart, {
                  [styles.highlight]: option.parts.at(-1)?.isMatch,
                })}
                title={option.parts.at(-1)?.substring}
              >
                {option.parts.at(-1)?.substring}
              </span>
            </div>
          </li>
        );
      }}
      size="small"
      value={selection}
    />
  );
});
